/**
 * CLI subcommand for querying workflow run results.
 *
 * Usage:
 *   canary workflow runs list <workflowId> [options]
 *   canary workflow runs get <workflowId> <runId> [options]
 *   canary workflow runs diagnostics <workflowId> <runId> [options]
 *
 * @module WorkflowRunsCLI
 */

import process from 'node:process';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';
import { statusIcon, formatRelative, computeDuration, pad } from './cli-format.js';
import {
  formatCacheOnlyFailure,
  getCacheOnlyFailure,
} from './cache-only-failure.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Pagination = {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
};

type RunSummary = {
  id: string;
  status: string;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  browserMode: string | null;
  stepTotals: { total: number; passed: number; failed: number } | null;
  snapshotVersion: number | null;
};

type RunListResponse = {
  ok: boolean;
  error?: string;
  data: { runs: RunSummary[] };
  pagination: Pagination;
};

type RunStep = {
  id: string;
  runId: string;
  nodeId: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  orderIndex: number;
  metadataJson: Record<string, unknown> | null;
  createdAt: string;
};

type RunDetail = {
  id: string;
  workflowId: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  metadataJson: Record<string, unknown> | null;
  steps: RunStep[];
  draftSnapshot: { id: string; version: string; basePublishedVersion: number } | null;
};

type RunDetailResponse = {
  ok: boolean;
  error?: string;
  data: { run: RunDetail };
};

type DiagnosticJson = {
  category?: string;
  title?: string;
  analysis?: string;
  recommendation?: string;
  smokingGun?: { type: string; description: string; detail?: string } | null;
};

type FailureDiagnosticResponse = {
  ok: boolean;
  error?: string;
  data: {
    id: string;
    status: string;
    diagnostic: DiagnosticJson | null;
    createdAt: string;
    updatedAt: string;
  } | null;
};

type RawDiagnosticsResponse = {
  ok: boolean;
  error?: string;
  consoleLogs: Array<{ level: string; text: string; timestamp?: string }>;
  networkRequests: Array<{ method: string; url: string; status: number }>;
};

// ---------------------------------------------------------------------------
// Handlers
// ---------------------------------------------------------------------------

type AnnotatedRun = RunSummary & { side: 'browser' | 'api' };

type ApiSequenceRunRow = {
  id: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  createdAt?: string;
};

type ApiSequenceByWorkflow = {
  ok: boolean;
  error?: string;
  data?: { id: string } | null;
};

type ApiSequenceRunsResponse = {
  ok: boolean;
  error?: string;
  data: ApiSequenceRunRow[];
  pagination?: Pagination;
};

async function fetchBrowserRuns(
  apiUrl: string,
  token: string,
  workflowId: string,
  page: string,
  pageSize: string
): Promise<{ runs: RunSummary[]; pagination: Pagination } | { error: string }> {
  const params = new URLSearchParams({ page, pageSize });
  const result = await apiRequest<RunListResponse>(
    apiUrl,
    token,
    'GET',
    `/workflows/${workflowId}/runs?${params}`
  );
  if (!result.ok) return { error: result.error ?? 'Unknown error' };
  return { runs: result.data.runs, pagination: result.pagination };
}

async function fetchApiRuns(
  apiUrl: string,
  token: string,
  workflowId: string,
  page: string,
  pageSize: string
): Promise<
  { runs: RunSummary[]; pagination: Pagination | null } | { error: string; code?: string }
> {
  const seqRes = await apiRequest<ApiSequenceByWorkflow & { code?: string }>(
    apiUrl,
    token,
    'GET',
    `/api-sequences/by-workflow/${workflowId}`
  );
  if (!seqRes.ok) {
    return {
      error: seqRes.error ?? 'Unknown error',
      ...(seqRes.code ? { code: seqRes.code } : {}),
    };
  }
  if (!seqRes.data) return { runs: [], pagination: null };
  const params = new URLSearchParams({ page, pageSize });
  const runsRes = await apiRequest<ApiSequenceRunsResponse>(
    apiUrl,
    token,
    'GET',
    `/api-sequences/${seqRes.data.id}/runs?${params}`
  );
  if (!runsRes.ok) return { error: runsRes.error ?? 'Unknown error' };
  const runs: RunSummary[] = (runsRes.data ?? []).map((r) => ({
    id: r.id,
    status: r.status,
    createdAt: r.createdAt ?? r.startedAt ?? new Date(0).toISOString(),
    startedAt: r.startedAt,
    finishedAt: r.finishedAt,
    errorMessage: r.errorMessage,
    browserMode: null,
    stepTotals: null,
    snapshotVersion: null,
  }));
  return { runs, pagination: runsRes.pagination ?? null };
}

async function handleRunsList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID. Usage: canary workflow runs list <workflowId>');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');
  const page = getArgValue(argv, '--page') ?? '1';
  const pageSize = getArgValue(argv, '--page-size') ?? '10';
  const explicitSide = getArgValue(argv, '--side') ?? getArgValue(argv, '--mode');
  const sideArg = (explicitSide ?? 'all').toLowerCase();

  if (!['all', 'api', 'browser'].includes(sideArg)) {
    console.error(`Error: --side must be one of: all, api, browser (got "${sideArg}")`);
    process.exit(1);
  }

  const annotated: AnnotatedRun[] = [];
  let browserPagination: Pagination | null = null;
  let apiPagination: Pagination | null = null;

  if (sideArg === 'browser' || sideArg === 'all') {
    const browser = await fetchBrowserRuns(apiUrl, token, workflowId, page, pageSize);
    if ('error' in browser) {
      console.error(`Error: ${browser.error}`);
      process.exit(1);
    }
    browserPagination = browser.pagination;
    for (const r of browser.runs) annotated.push({ ...r, side: 'browser' });
  }

  if (sideArg === 'api' || sideArg === 'all') {
    const api = await fetchApiRuns(apiUrl, token, workflowId, page, pageSize);
    if ('error' in api) {
      // When --side wasn't explicit and the workflow simply has no api-sequence,
      // don't hard-fail the whole listing — just skip the api leg.
      const isMissingSequence = api.code === 'API_SEQUENCE_NOT_FOUND';
      if (!explicitSide && isMissingSequence) {
        if (!jsonOutput) {
          console.error('Note: workflow has no api-sequence — showing browser runs only.');
        }
      } else {
        console.error(`Error: ${api.error}`);
        process.exit(1);
      }
    } else {
      apiPagination = api.pagination;
      for (const r of api.runs) annotated.push({ ...r, side: 'api' });
    }
  }

  annotated.sort((a, b) => {
    const ta = new Date(a.startedAt ?? a.createdAt).getTime();
    const tb = new Date(b.startedAt ?? b.createdAt).getTime();
    return tb - ta;
  });

  if (jsonOutput) {
    console.log(
      JSON.stringify(
        {
          ok: true,
          data: { runs: annotated },
          pagination: { browser: browserPagination, api: apiPagination },
        },
        null,
        2
      )
    );
    return;
  }

  if (annotated.length === 0) {
    console.log('No runs found.');
    return;
  }

  console.log(`Runs (${annotated.length} shown, side=${sideArg})\n`);
  const idW = 36;
  const sideW = 8;
  const statusW = 12;
  const durationW = 10;
  console.log(
    [
      'ID'.padEnd(idW),
      'Side'.padEnd(sideW),
      'Status'.padEnd(statusW),
      'Duration'.padEnd(durationW),
      'Started',
    ].join('  ')
  );

  for (const run of annotated) {
    const icon = statusIcon(run.status);
    const dur = computeDuration(run.startedAt, run.finishedAt);
    const time = formatRelative(run.startedAt ?? run.createdAt);
    console.log(
      [
        run.id.padEnd(idW),
        pad(run.side, sideW),
        `${icon} ${pad(run.status, statusW - 3)}`,
        pad(dur, durationW),
        time,
      ].join('  ')
    );
  }
  console.log('');
}

async function handleRunsGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  if (positional.length < 2) {
    console.error(
      'Error: Missing arguments. Usage: canary workflow runs get <workflowId> <runId>'
    );
    process.exit(1);
  }

  const [workflowId, runId] = positional;
  const jsonOutput = hasFlag(argv, '--json');

  const result = await apiRequest<RunDetailResponse>(
    apiUrl,
    token,
    'GET',
    `/workflows/${workflowId}/runs/${runId}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  const { run } = result.data;
  const duration = computeDuration(run.startedAt, run.finishedAt);
  const cacheOnly = run.metadataJson?.cacheOnly === true;
  const cacheOnlyFailure = getCacheOnlyFailure(run.metadataJson);

  console.log(`Run:      ${run.id}`);
  console.log(`Status:   ${statusIcon(run.status)} ${run.status}`);
  if (cacheOnly) {
    console.log('Mode:     cache-only');
  }
  console.log(`Duration: ${duration}`);

  if (run.errorMessage) {
    console.log(`Error:    ${run.errorMessage}`);
  }

  if (run.draftSnapshot) {
    console.log(`Snapshot: v${run.draftSnapshot.version} (base: v${run.draftSnapshot.basePublishedVersion})`);
  }

  if (cacheOnlyFailure) {
    console.log('');
    for (const line of formatCacheOnlyFailure(cacheOnlyFailure)) {
      console.log(line);
    }
  }

  const steps = (run.steps ?? []).sort((a, b) => a.orderIndex - b.orderIndex);

  if (steps.length === 0) {
    console.log('\nNo steps recorded.');
    return;
  }

  console.log(`\nSteps (${steps.length}):`);
  for (const step of steps) {
    const idx = String(step.orderIndex + 1).padStart(2);
    const icon = statusIcon(step.status);
    const nodeId = step.nodeId.slice(0, 8);
    const dur = computeDuration(step.startedAt, step.finishedAt);

    console.log(`  ${idx}. ${icon}  ${pad(nodeId, 10)} ${dur}`);

    if (step.errorMessage) {
      console.log(`       Error: ${step.errorMessage}`);
    }
  }

  console.log('');
}

async function handleRunsDiagnostics(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  if (positional.length < 2) {
    console.error(
      'Error: Missing arguments. Usage: canary workflow runs diagnostics <workflowId> <runId>'
    );
    process.exit(1);
  }

  const [workflowId, runId] = positional;
  const jsonOutput = hasFlag(argv, '--json');

  // Try AI failure diagnostic first
  const result = await apiRequest<FailureDiagnosticResponse>(
    apiUrl,
    token,
    'GET',
    `/workflows/${workflowId}/runs/${runId}/failure-diagnostic`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (jsonOutput && result.data) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  if (result.data) {
    const { status, diagnostic } = result.data;

    if (status === 'pending' || status === 'running') {
      console.log(`Diagnostic is still in progress (status: ${status}).`);
      return;
    }

    if (status === 'failed') {
      console.log('Diagnostic analysis failed.');
      return;
    }

    if (diagnostic) {
      console.log('Failure Diagnostic');
      console.log(`Status:     ${status}`);
      if (diagnostic.category) {
        console.log(`Category:   ${diagnostic.category}`);
      }
      if (diagnostic.title) {
        console.log(`Title:      ${diagnostic.title}`);
      }

      if (diagnostic.analysis) {
        console.log('');
        console.log('Analysis:');
        console.log(`  ${diagnostic.analysis}`);
      }

      if (diagnostic.recommendation) {
        console.log('');
        console.log('Recommendation:');
        console.log(`  ${diagnostic.recommendation}`);
      }

      if (diagnostic.smokingGun) {
        console.log('');
        console.log('Smoking Gun:');
        console.log(`  > ${diagnostic.smokingGun.description}`);
        if (diagnostic.smokingGun.detail) {
          console.log(`    ${diagnostic.smokingGun.detail}`);
        }
      }

      console.log('');
      return;
    }
  }

  // Fall back to raw console/network diagnostics
  const rawResult = await apiRequest<RawDiagnosticsResponse>(
    apiUrl,
    token,
    'GET',
    `/workflows/${workflowId}/runs/${runId}/diagnostics`
  );

  if (!rawResult.ok) {
    console.log('No diagnostic available for this run.');
    return;
  }

  if (jsonOutput) {
    console.log(JSON.stringify(rawResult, null, 2));
    return;
  }

  const hasConsole = rawResult.consoleLogs && rawResult.consoleLogs.length > 0;
  const hasNetwork = rawResult.networkRequests && rawResult.networkRequests.length > 0;

  if (!hasConsole && !hasNetwork) {
    console.log('No diagnostic data available for this run.');
    return;
  }

  if (hasConsole) {
    console.log(`Console Logs (${rawResult.consoleLogs.length}):`);
    for (const log of rawResult.consoleLogs.slice(0, 50)) {
      const level = log.level.toUpperCase().padEnd(7);
      console.log(`  [${level}] ${log.text}`);
    }
    if (rawResult.consoleLogs.length > 50) {
      console.log(`  ... and ${rawResult.consoleLogs.length - 50} more`);
    }
    console.log('');
  }

  if (hasNetwork) {
    console.log(`Network Requests (${rawResult.networkRequests.length}):`);
    for (const req of rawResult.networkRequests.slice(0, 30)) {
      const statusStr = req.status >= 400 ? `${req.status} !!!` : String(req.status);
      console.log(`  ${req.method.padEnd(6)} ${statusStr.padEnd(8)} ${req.url}`);
    }
    if (rawResult.networkRequests.length > 30) {
      console.log(`  ... and ${rawResult.networkRequests.length - 30} more`);
    }
    console.log('');
  }
}

// ---------------------------------------------------------------------------
// Help
// ---------------------------------------------------------------------------

function printRunsHelp(): void {
  console.log(
    [
      'Usage: canary workflow runs <sub-command> [options]',
      '',
      'Sub-commands:',
      '  list <workflowId> [options]                    List runs for a workflow',
      '  get <workflowId> <runId> [options]             Get run detail with steps',
      '  diagnostics <workflowId> <runId> [options]     Get failure diagnostic',
      '',
      'List options:',
      '  --page <n>            Page number (default: 1)',
      '  --page-size <n>       Page size (default: 10)',
      '  --side <side>         Filter: browser, api, all (default: all). Alias: --mode',
      '',
      'Output options:',
      '  --json                Output raw JSON',
      '',
      'Common options:',
      '  --env <env>           Target environment (prod, dev, local)',
      '  --api-url <url>       API URL override',
      '  --token <key>         API token override',
    ].join('\n')
  );
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export async function runWorkflowRuns(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printRunsHelp();
    return;
  }

  switch (subCommand) {
    case 'list':
      await handleRunsList(rest, apiUrl, token);
      break;
    case 'get':
      await handleRunsGet(rest, apiUrl, token);
      break;
    case 'diagnostics':
      await handleRunsDiagnostics(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printRunsHelp();
      process.exit(1);
  }
}
